import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material'
import {
  RadioButtonUnchecked,
  RadioButtonChecked,
  Search as SearchIcon
} from '@mui/icons-material'
import { api } from '../../services/api'
import { debugLog } from '../../utils/debugLog'
import { generateAdminFpUrl } from '../../utils/urls'
import { poolwerxColors } from '../../theme/colors'

interface XeroTenant {
  tenantId: string
  tenantName: string
  tenantType: string
  createdDateUtc?: string
}

interface SessionData {
  fp_code: string
  /** `franchise_partners.id` - the admin connections route is id-only. */
  fp_id?: number
  fp_name?: string
  tenants: XeroTenant[]
  access_token: string
  refresh_token: string
  expires_at: string
}

const XeroTenantSelection: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [sessionData, setSessionData] = useState<SessionData | null>(null)
  const [selectedTenant, setSelectedTenant] = useState<XeroTenant | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const loadSessionData = async () => {
      // Check for legacy base64 session parameter (for backward compatibility)
      const sessionParam = searchParams.get('session')
      const sessionIdParam = searchParams.get('session_id')
      
      if (!sessionParam && !sessionIdParam) {
        setError('❌ No session data found. Please restart the connection process.')
        return
      }

      // Test mode (legacy parameter)
      if (sessionParam === 'test') {
        const testData: SessionData = {
          fp_code: 'test_fp',
          fp_name: 'Test Franchise Partner',
          tenants: [
            {
              tenantId: 'test-tenant-1',
              tenantName: 'Test Company Pty Ltd',
              tenantType: 'ORGANISATION',
              createdDateUtc: '2023-01-15T10:30:00Z'
            },
            {
              tenantId: 'test-tenant-2',
              tenantName: 'Demo Business Services',
              tenantType: 'ORGANISATION',
              createdDateUtc: '2023-03-20T14:45:00Z'
            },
            {
              tenantId: 'test-tenant-3',
              tenantName: 'Sample Franchise Operations',
              tenantType: 'ORGANISATION',
              createdDateUtc: '2023-06-10T08:15:00Z'
            }
          ],
          access_token: 'test-token',
          refresh_token: 'test-refresh',
          expires_at: new Date(Date.now() + 3600000).toISOString()
        }
        setSessionData(testData)
        // Pre-fill search with franchise partner name
        const searchPreFill = testData.fp_name || testData.fp_code
        setSearchTerm(searchPreFill)
        return
      }

      try {
        let data: SessionData

        if (sessionIdParam) {
          // New approach: Fetch session data from API using session ID
          const response = await api.get(`/connections/xero/session/${sessionIdParam}`)
          
          if (!response.success) {
            throw new Error(response.message || 'Failed to retrieve session data')
          }
          
          data = response.data
        } else if (sessionParam) {
          // Legacy approach: Decode base64 session data from URL
          const decodedSession = atob(sessionParam)
          data = JSON.parse(decodedSession)
        } else {
          throw new Error('No valid session parameter found')
        }
        
        if (!data.fp_code || !data.tenants || !Array.isArray(data.tenants)) {
          throw new Error('Invalid session data structure')
        }
        
        setSessionData(data)
        // Pre-fill search with franchise partner name
        const searchPreFill = data.fp_name || data.fp_code
        setSearchTerm(searchPreFill)
        
      } catch (err) {
        console.error('Failed to load session data:', err)
        if (err instanceof Error) {
          setError(`❌ ${err.message}`)
        } else {
          setError('❌ Failed to load session data. Please restart the connection process.')
        }
      }
    }

    loadSessionData()
  }, [searchParams])

  // Focus search input and position cursor at end when data is loaded
  useEffect(() => {
    if (sessionData && searchInputRef.current) {
      searchInputRef.current.focus()
      // Set cursor to end of text
      const length = searchTerm.length
      searchInputRef.current.setSelectionRange(length, length)
    }
  }, [sessionData, searchTerm])

  const handleTenantSelect = (tenant: XeroTenant) => {
    setSelectedTenant(tenant)
  }

  const handleConnect = async () => {
    if (!sessionData || !selectedTenant) return

    setConnecting(true)
    setError(null)

    // Test mode - don't make actual API call
    if (searchParams.get('session') === 'test') {
      debugLog('✅ Test mode - simulated Xero connection:', {
        fp_code: sessionData.fp_code,
        tenant: selectedTenant
      })
      
      // Simulate success delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      navigate(`/admin/manage-connections/${sessionData.fp_code}`, { 
        state: { 
          message: `✅ [TEST MODE] Successfully connected ${sessionData.fp_code.toUpperCase()}${sessionData.fp_name ? ` (${sessionData.fp_name})` : ''} to Xero organization "${selectedTenant.tenantName}"` 
        } 
      })
      return
    }

    try {
      const response = await api.post('/connections/xero/select-tenant', {
        session_data: sessionData,  // Pass session data directly as object
        tenant_id: selectedTenant.tenantId,
        tenant_name: selectedTenant.tenantName
      })

      // The admin connections page is addressed by the FP's DB id (id-only
      // route), so pin the navigation by fp_id carried in the session.
      const connectionsUrl = generateAdminFpUrl(sessionData.fp_id ?? '', 'connections')
      debugLog('✅ Xero connection successful')
      debugLog('Navigating to:', connectionsUrl)
      
      // Extract connection test results from response
      const connectionTest = response.data?.connection_test
      const navigationState: any = {}
      
      if (connectionTest) {
        if (connectionTest.status === 'connected') {
          navigationState.connectionTestSuccess = {
            message: `✅ Connection test successful! Connected to ${connectionTest.organization_name || selectedTenant.tenantName}`,
            system: 'xero',
            responseTime: connectionTest.response_time_ms
          }
        } else {
          navigationState.connectionTestError = {
            message: `❌ Connection test failed: ${connectionTest.error_message || 'Unknown error'}`,
            system: 'xero',
            status: connectionTest.status
          }
        }
      }
      
      // Navigate after a short delay to ensure the response is processed
      setTimeout(() => {
        // Try to navigate with React Router first to pass state
        try {
          navigate(connectionsUrl, { state: navigationState })
        } catch (e) {
          // Fallback to window.location.href
          window.location.href = connectionsUrl
        }
      }, 100)
      
    } catch (err: any) {
      console.error('❌ Failed to connect Xero:', err)
      setError(
        err.response?.data?.detail || 
        err.message || 
        'Failed to connect to Xero. Please try again.'
      )
    } finally {
      setConnecting(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/franchise-partners')
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown'
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return 'Unknown'
    }
  }

  // Filter and sort tenants based on search term
  const filteredTenants = (sessionData?.tenants.filter(tenant =>
    tenant.tenantName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []).sort((a, b) => a.tenantName.localeCompare(b.tenantName))

  // Highlight matching text
  const highlightText = (text: string, search: string) => {
    if (!search.trim()) return text
    
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: 'yellow', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : part
    )
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Xero Connection Error - Inspector Tool</title>
        </Helmet>
        
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          
          <Box sx={{ textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={handleCancel}
              sx={{
                borderColor: poolwerxColors.teal,
                color: poolwerxColors.teal,
                '&:hover': {
                  borderColor: poolwerxColors.teal,
                  backgroundColor: poolwerxColors.blueLight
                }
              }}
            >
              Return to Franchise Partners
            </Button>
          </Box>
        </Box>
      </>
    )
  }

  if (!sessionData) {
    return (
      <>
        <Helmet>
          <title>Loading Xero Connection - Inspector Tool</title>
        </Helmet>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Select Xero Organization - Inspector Tool</title>
      </Helmet>

      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: poolwerxColors.teal }}>
          🔗 Connect Xero
        </Typography>
        

        <Alert severity="warning" sx={{ mb: 3, border: `1px solid ${poolwerxColors.red}` }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            ⚠️ IMPORTANT: You must select the correct Xero organization
          </Typography>
          <Typography variant="body2">
            Selecting the wrong organization will cause connection issues and data sync problems.
          </Typography>
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: poolwerxColors.teal }}>
              📋 Find and Select Your Organization
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Search below to find the Xero organization for <strong>{sessionData.fp_name ? `${sessionData.fp_name} (${sessionData.fp_code.toUpperCase()})` : sessionData.fp_code.toUpperCase()}</strong>
            </Typography>
            
            {sessionData.tenants.length > 0 && (
              <TextField
                fullWidth
                placeholder={`Search for ${sessionData.fp_name ? `${sessionData.fp_name} (${sessionData.fp_code.toUpperCase()})` : sessionData.fp_code.toUpperCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                inputRef={searchInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            
            {sessionData.tenants.length === 0 ? (
              <Alert severity="warning">
                No Xero organizations found for this connection. Please ensure you have access to at least one Xero organization.
              </Alert>
            ) : filteredTenants.length === 0 ? (
              <Alert severity="info">
                No organizations match your search. Try a different search term.
              </Alert>
            ) : (
              <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {filteredTenants.map((tenant, index) => (
                  <React.Fragment key={tenant.tenantId}>
                    {index > 0 && <Divider />}
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedTenant?.tenantId === tenant.tenantId}
                        onClick={() => handleTenantSelect(tenant)}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: poolwerxColors.blueLight,
                            '&:hover': {
                              backgroundColor: poolwerxColors.blueLight
                            }
                          }
                        }}
                      >
                        <ListItemIcon>
                          {selectedTenant?.tenantId === tenant.tenantId ? (
                            <RadioButtonChecked sx={{ color: poolwerxColors.teal }} />
                          ) : (
                            <RadioButtonUnchecked sx={{ color: poolwerxColors.grey[400] }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography component="span" sx={{ fontWeight: 600 }}>
                              {highlightText(tenant.tenantName, searchTerm)}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" color="textSecondary" component="span" display="block">
                                Type: {tenant.tenantType}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" component="span" display="block">
                                Created: {formatDate(tenant.createdDateUtc)}
                              </Typography>
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={!selectedTenant || connecting || sessionData.tenants.length === 0}
            startIcon={connecting ? <CircularProgress size={16} /> : undefined}
            sx={{
              backgroundColor: poolwerxColors.teal,
              '&:hover': {
                backgroundColor: poolwerxColors.teal,
                filter: 'brightness(0.9)'
              },
              '&:disabled': {
                backgroundColor: poolwerxColors.grey[300]
              }
            }}
          >
            {connecting ? 'Connecting...' : 'Connect to Xero'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={connecting}
            sx={{
              borderColor: poolwerxColors.grey[400],
              color: poolwerxColors.grey[600],
              '&:hover': {
                borderColor: poolwerxColors.grey[600],
                backgroundColor: poolwerxColors.grey[50]
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default XeroTenantSelection